import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import Cookies from 'js-cookie';
import { IS_BFF, applyBffAuth } from './auth-mode';
import { refreshAccessToken, endSessionIfSuspended } from './refresh';

/**
 * Dedicated HTTP client for the Knowledge Base ("files") module.
 *
 * The Knowledge Base is served by the standalone Python `data-service` (the same
 * backend Wexa Studio uses), reused as-is. File binaries live in Azure blob
 * storage and embeddings are produced by the data-service ingest → Qdrant
 * pipeline. The context-service (Go) backend is NOT involved.
 *
 * In the browser, requests go to `/data-api/*` which `next.config.js` rewrites to
 * NEXT_PUBLIC_DATA_SERVICE_URL. Auth mirrors the Context Service `apiClient` and
 * the Tables client (Bearer token from localStorage/cookie + x-server-key + stsk),
 * because the data-service accepts the same identity-service token and server key.
 *
 * This client is intentionally self-contained and parallel to tables-client.ts.
 */

const isBrowser = typeof window !== 'undefined';
const DATA_API_BASE_URL = isBrowser
  ? '/data-api'
  : process.env.NEXT_PUBLIC_DATA_SERVICE_URL || 'http://localhost:4000';
const COOKIE_NAME =
  process.env.NEXT_PUBLIC_COOKIE_NAME || process.env.COOKIE_NAME || 'wexa_dev_cookie';

class KnowledgeBaseClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: DATA_API_BASE_URL,
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
    });

    // Request interceptor: attach ONLY the user's bearer token — exactly like
    // Wexa Studio's data-service axios instance.
    //
    // IMPORTANT: do NOT send the `stsk` server key here. The data-service's
    // check_authorization treats a valid `stsk` as a trusted server/system call
    // and returns an AuthenticatedUser with project=None/organization=None. The
    // file routes (upload/ingest) then reject with
    // "User does not have access to any project". Sending only the Bearer token
    // makes the data-service resolve the REAL logged-in user — with their project
    // and organization — via the authorization service (using the projectID query
    // param as x-project-id), which is how Wexa Studio works.
    this.client.interceptors.request.use(
      async (config) => {
        // bff mode: no JS token; BFF injects Authorization from the httpOnly cookie.
        if (IS_BFF) return applyBffAuth(config);
        // Token source: COOKIE FIRST, exactly like Wexa Studio's data-service
        // axios instance (and the identity client). This is critical: the cookie
        // holds the current session token, whereas `localStorage.auth_token` is a
        // write-once cache that goes STALE after a token rotation/re-login. Using
        // the stale localStorage token makes the data-service authorize the wrong
        // session → "User does not have access to any project". Preferring the
        // cookie keeps us on the same fresh token wexa-web uses.
        const cookieToken = Cookies.get(COOKIE_NAME);
        const token =
          cookieToken ||
          (typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null);

        if (!token) {
          // Reject as an AxiosError (not a bare Error) so it carries the config
          // and flows through response handling/.catch() consistently with the
          // context-service client. Message is preserved for callers that key on
          // it (e.g. the KB project probe distinguishing "no token" from "no
          // access").
          return Promise.reject(
            new axios.AxiosError('No authentication token', 'ERR_AUTH_MISSING', config),
          );
        }

        config.headers.Authorization = `Bearer ${token}`;
        return config;
      },
      (error) => Promise.reject(error),
    );

    // Response interceptor: on 401, do a single-flight token refresh and retry
    // once — mirroring the context-service client and dataservice-client. Without
    // this the KB module was the ONLY client that didn't honor access-token
    // rotation: once the short-lived token expired it 401'd forever (surfacing as
    // a spurious "no accessible Knowledge Base") while the rest of the app kept
    // working by silently refreshing. A failed refresh clears the session and
    // AuthProvider redirects to /login (see refresh.ts).
    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const original = error.config as
          | (AxiosRequestConfig & { _retry?: boolean })
          | undefined;
        if (error.response?.status === 401 && original && !original._retry) {
          original._retry = true;
          const newToken = await refreshAccessToken();
          if (newToken) {
            // Re-issue through the instance so the request interceptor re-runs
            // and attaches the refreshed token.
            return this.client(original);
          }
          console.warn('[KnowledgeBase Client] 401 and refresh failed:', original?.url);
        }
        // FAB-303: a suspended member must be signed out, not left
        // seeing bare errors on actions the UI still offers.
        endSessionIfSuspended(error);
        return Promise.reject(error);
      },
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  /** DELETE with an optional request body (data-service deletes send a body). */
  async delete<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, { ...config, data });
    return response.data;
  }
}

export const knowledgeBaseClient = new KnowledgeBaseClient();
